import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { faqConfigFromSlot } from "./chatbotSlotFaq";
import { acoesFromSlot } from "./chatbotSlotActions";
import {
  buildIdentPath,
  collectFlowReferences,
  parseAutomationRuleConfig,
  REF_KIND_AGENT,
  REF_KIND_AUTOMATION,
  REF_KIND_CHANNEL,
  REF_KIND_DELIVERY,
  REF_KIND_PAYMENT,
  REF_KIND_POSITION,
  REF_KIND_SUBTYPE,
  REF_KIND_TYPE,
  REF_KIND_USER,
} from "./chatbotFlowReferences";

export const CHATBOT_FLOW_FORMAT = "xpro4-chatbot-flow";
export const CHATBOT_FLOW_VERSION = 3;

const EXPORT_SLOT_FIELDS = [
  "identificador",
  "nome",
  "resposta",
  "botoes",
  "condicoes",
  "tipo_acao",
  "id_acao",
  "urlapi",
  "evento",
  "acoes",
  "resposta_modo",
  "faq_ia",
  "agente_ia",
  "global",
  "ignorarglobal",
  "obrigatorio",
  "ordem",
  "arquivo",
];

const EXCEL_HEADERS = [
  "caminho",
  "identificador",
  "nome",
  "resposta",
  "botoes",
  "condicoes",
  "tipo_acao",
  "id_acao",
  "urlapi",
  "evento",
  "acoes",
  "global",
  "ignorarglobal",
  "ordem",
  "arquivo",
];

function stripHtml(text) {
  if (!text) return "";
  return String(text)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function enrichActionsForExport(acoes, slotById) {
  return (acoes || []).map((action) => {
    const item = { ...action };
    const tipo = Number(item.tipo_acao) || 0;
    const idAcao = Number(item.id_acao) || 0;
    if (tipo === 2 && idAcao && slotById?.size) {
      item.navPath = buildIdentPath(idAcao, slotById);
      item.sourceSlotId = idAcao;
    }
    return item;
  });
}

function serializeSlot(slot, slotById) {
  const faqCfg = faqConfigFromSlot(slot);
  const acoes = enrichActionsForExport(acoesFromSlot(slot), slotById);
  const out = {};
  for (const key of EXPORT_SLOT_FIELDS) {
    if (key === "identificador") {
      out.identificador = slot.identificador != null ? String(slot.identificador).trim() : "";
      continue;
    }
    if (key === "botoes") {
      const raw = slot.botoes;
      out.botoes = Array.isArray(raw)
        ? raw.map((b) => String(b || "").trim()).filter(Boolean)
        : raw
          ? String(raw)
              .split(",")
              .map((b) => b.trim())
              .filter(Boolean)
          : [];
      continue;
    }
    if (key === "global" || key === "ignorarglobal" || key === "obrigatorio") {
      if (key === "obrigatorio") {
        out.obrigatorio = !!faqCfg.obrigatorio;
        continue;
      }
      out[key] = !!(slot[key] === true || slot[key] === 1 || slot[key] === "1");
      continue;
    }
    if (key === "evento") {
      const evento = slot.evento;
      if (evento && typeof evento === "object" && String(evento.tipo || "").trim()) {
        out.evento = evento;
      }
      continue;
    }
    if (key === "acoes") {
      const cleaned = acoes
        .filter((a) => Number(a?.tipo_acao) && Number(a?.id_acao))
        .map(({ tipo_acao, id_acao, values, labels, urlapi, navPath, sourceSlotId }) => {
          const item = {
            tipo_acao: Number(tipo_acao),
            id_acao: Number(id_acao),
          };
          if (values && typeof values === "object" && Object.keys(values).length) {
            item.values = values;
          }
          if (labels && typeof labels === "object" && Object.keys(labels).length) {
            item.labels = labels;
          }
          if (urlapi && String(urlapi).trim()) item.urlapi = String(urlapi).trim();
          if (Array.isArray(navPath) && navPath.length) item.navPath = navPath;
          if (sourceSlotId) item.sourceSlotId = sourceSlotId;
          return item;
        });
      if (cleaned.length) out.acoes = cleaned;
      continue;
    }
    if (key === "resposta_modo") {
      out.resposta_modo = faqCfg.resposta_modo;
      continue;
    }
    if (key === "faq_ia") {
      if (faqCfg.faq_ia?.source_urls?.length || faqCfg.faq_ia?.documents?.length || faqCfg.faq_ia?.faq_answer_focus) {
        out.faq_ia = faqCfg.faq_ia;
      }
      continue;
    }
    if (key === "agente_ia") {
      if (faqCfg.agente_ia?.agent_id || faqCfg.resposta_modo === "agente_ia") {
        out.agente_ia = faqCfg.agente_ia;
      }
      continue;
    }
    if (slot[key] !== undefined && slot[key] !== null) {
      out[key] = slot[key];
    }
  }
  if (slot.id != null && Number(slot.id) > 0) out.sourceId = Number(slot.id);
  return out;
}

function groupSlotsByParent(slots) {
  const byParent = new Map();
  for (const slot of slots || []) {
    const pid = slot.id_pai != null && slot.id_pai !== "" ? String(slot.id_pai) : "0";
    if (!byParent.has(pid)) byParent.set(pid, []);
    byParent.get(pid).push(slot);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => (Number(a.ordem) || 0) - (Number(b.ordem) || 0) || Number(a.id) - Number(b.id));
  }
  return byParent;
}

function buildSlotTree(slots) {
  const byParent = groupSlotsByParent(slots);
  const slotById = new Map((slots || []).map((s) => [Number(s.id), s]));

  function buildNode(slot) {
    const children = byParent.get(String(slot.id)) || [];
    const regularChildren = [];
    let exception = null;
    for (const child of children) {
      if (String(child.identificador || "").trim() === "Exceção") {
        exception = serializeSlot(child, slotById);
      } else {
        regularChildren.push(buildNode(child));
      }
    }
    const node = { ...serializeSlot(slot, slotById), children: regularChildren };
    if (exception) node.exception = exception;
    return node;
  }

  const roots = (byParent.get("0") || []).filter((s) => String(s.identificador || "").trim() !== "Exceção");
  return roots.map(buildNode);
}

export function slotsToFlowDocument(slots, { chatbotName = "", sourceUserId = null } = {}) {
  return {
    version: CHATBOT_FLOW_VERSION,
    format: CHATBOT_FLOW_FORMAT,
    exportedAt: new Date().toISOString(),
    chatbotName: chatbotName || "",
    sourceUserId: sourceUserId || null,
    slots: buildSlotTree(slots),
  };
}

async function fetchRefSnapshots(refs, loaders = {}) {
  const out = {};
  const {
    fetchAutomationRule,
    fetchUser,
    fetchAgent,
    fetchChannel,
    fetchType,
    fetchSubtype,
    fetchPaymentMethod,
    fetchDeliveryMethod,
    fetchPosition,
  } = loaders;

  const tasks = [];

  for (const [id, meta] of Object.entries(refs[REF_KIND_AUTOMATION] || {})) {
    if (!fetchAutomationRule) {
      out[REF_KIND_AUTOMATION] = out[REF_KIND_AUTOMATION] || {};
      out[REF_KIND_AUTOMATION][id] = { id: Number(id), label: meta.label, rule: null };
      continue;
    }
    tasks.push(
      fetchAutomationRule(id)
        .then((row) => {
          out[REF_KIND_AUTOMATION] = out[REF_KIND_AUTOMATION] || {};
          out[REF_KIND_AUTOMATION][id] = {
            id: Number(id),
            label: row?.descricao || meta.label,
            rule: parseAutomationRuleConfig(row),
          };
        })
        .catch(() => {
          out[REF_KIND_AUTOMATION] = out[REF_KIND_AUTOMATION] || {};
          out[REF_KIND_AUTOMATION][id] = { id: Number(id), label: meta.label, rule: null };
        })
    );
  }

  const entityFetchers = [
    [REF_KIND_USER, fetchUser, "nome"],
    [REF_KIND_AGENT, fetchAgent, "name"],
    [REF_KIND_CHANNEL, fetchChannel, "nome"],
    [REF_KIND_TYPE, fetchType, "nome"],
    [REF_KIND_SUBTYPE, fetchSubtype, "nome"],
    [REF_KIND_PAYMENT, fetchPaymentMethod, "nome"],
    [REF_KIND_DELIVERY, fetchDeliveryMethod, "nome"],
    [REF_KIND_POSITION, fetchPosition, "nome"],
  ];

  for (const [kind, fetcher, labelKey] of entityFetchers) {
    for (const [id, meta] of Object.entries(refs[kind] || {})) {
      if (!fetcher) {
        out[kind] = out[kind] || {};
        out[kind][id] = { id, label: meta.label, snapshot: null };
        continue;
      }
      tasks.push(
        fetcher(id)
          .then((row) => {
            out[kind] = out[kind] || {};
            out[kind][id] = {
              id,
              label: row?.[labelKey] || row?.nome || meta.label,
              snapshot: row || null,
            };
          })
          .catch(() => {
            out[kind] = out[kind] || {};
            out[kind][id] = { id, label: meta.label, snapshot: null };
          })
      );
    }
  }

  await Promise.all(tasks);
  return out;
}

/** Exportação completa com referências externas para clonagem entre workspaces. */
export async function buildFullFlowDocument(slots, options = {}) {
  const {
    chatbotName = "",
    sourceUserId = null,
    parameterForm = null,
    loaders = {},
  } = options;

  const base = slotsToFlowDocument(slots, { chatbotName, sourceUserId });
  const refsCollected = collectFlowReferences({ slots, flowDoc: base, parameterForm });
  const refs = await fetchRefSnapshots(refsCollected, loaders);

  return {
    ...base,
    refs,
  };
}

function flattenTree(nodes, parentPath = "") {
  const rows = [];
  for (const node of nodes || []) {
    const identificador = String(node.identificador || "Nó").trim() || "Nó";
    const path = parentPath ? `${parentPath} > ${identificador}` : identificador;
    rows.push({
      caminho: path,
      identificador,
      nome: node.nome ?? "",
      resposta: node.resposta ?? "",
      botoes: Array.isArray(node.botoes) ? node.botoes.join(", ") : String(node.botoes || ""),
      condicoes: node.condicoes ?? "",
      tipo_acao: node.tipo_acao ?? 0,
      id_acao: node.id_acao ?? 0,
      urlapi: node.urlapi ?? "",
      evento: node.evento ? JSON.stringify(node.evento) : "",
      acoes: Array.isArray(node.acoes) && node.acoes.length ? JSON.stringify(node.acoes) : "",
      global: node.global ? 1 : 0,
      ignorarglobal: node.ignorarglobal ? 1 : 0,
      ordem: node.ordem ?? 0,
      arquivo: node.arquivo ?? "",
    });
    if (node.exception) {
      rows.push({
        caminho: `${path} > Exceção`,
        identificador: "Exceção",
        nome: node.exception.nome ?? "",
        resposta: node.exception.resposta ?? "",
        botoes: Array.isArray(node.exception.botoes) ? node.exception.botoes.join(", ") : "",
        condicoes: node.exception.condicoes ?? "",
        tipo_acao: node.exception.tipo_acao ?? 0,
        id_acao: node.exception.id_acao ?? 0,
        urlapi: node.exception.urlapi ?? "",
        global: node.exception.global ? 1 : 0,
        ignorarglobal: node.exception.ignorarglobal ? 1 : 0,
        ordem: node.exception.ordem ?? 1,
        arquivo: node.exception.arquivo ?? "",
      });
    }
    rows.push(...flattenTree(node.children, path));
  }
  return rows;
}

function parseBoolCell(value) {
  if (value === true || value === 1 || value === "1") return true;
  const s = String(value || "").trim().toLowerCase();
  return s === "sim" || s === "yes" || s === "true" || s === "s";
}

function parseBotoesCell(value) {
  if (Array.isArray(value)) return value.map((b) => String(b).trim()).filter(Boolean);
  if (!value) return [];
  return String(value)
    .split(/[,;|]/)
    .map((b) => b.trim())
    .filter(Boolean);
}

function parseJsonCell(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  const str = String(value).trim();
  if (!str) return null;
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
}

function rowToSlotNode(row) {
  const acoesRaw = parseJsonCell(row.acoes ?? row.Ações ?? row.acoes);
  const eventoRaw = parseJsonCell(row.evento ?? row.Evento ?? row.evento);
  return {
    identificador: String(row.identificador || row.Identificador || "Nó").trim() || "Nó",
    nome: String(row.nome ?? row.Nome ?? "").trim(),
    resposta: String(row.resposta ?? row.Resposta ?? ""),
    botoes: parseBotoesCell(row.botoes ?? row.Botões ?? row.botoes),
    condicoes: row.condicoes ?? row.Condições ?? row.condicoes ?? null,
    tipo_acao: Number(row.tipo_acao ?? row["Tipo Ação"] ?? 0) || 0,
    id_acao: Number(row.id_acao ?? row["ID Ação"] ?? 0) || 0,
    urlapi: row.urlapi ?? row["URL API"] ?? null,
    evento: eventoRaw,
    acoes: Array.isArray(acoesRaw) ? acoesRaw : null,
    global: parseBoolCell(row.global ?? row.Global),
    ignorarglobal: parseBoolCell(row.ignorarglobal ?? row["Ignora Global"]),
    ordem: Number(row.ordem ?? row.Ordem ?? 0) || 0,
    arquivo: row.arquivo ?? row.Arquivo ?? null,
    children: [],
  };
}

function excelRowsToFlowDocument(rows, chatbotName = "") {
  const normalized = (rows || [])
    .map((row) => {
      const mapped = {};
      EXCEL_HEADERS.forEach((key) => {
        const variants = [key, key.charAt(0).toUpperCase() + key.slice(1)];
        for (const v of variants) {
          if (row[v] !== undefined) {
            mapped[key] = row[v];
            break;
          }
        }
      });
      if (!mapped.identificador && row.identificador) mapped.identificador = row.identificador;
      if (!mapped.caminho && row.caminho) mapped.caminho = row.caminho;
      return mapped;
    })
    .filter((row) => String(row.identificador || "").trim());

  const pathMap = new Map();
  const roots = [];

  for (const row of normalized) {
    const path = String(row.caminho || row.identificador || "").trim();
    if (!path) continue;
    const segments = path.split(">").map((s) => s.trim()).filter(Boolean);
    if (!segments.length) continue;

    let parentChildren = roots;
    let builtPath = "";
    for (let i = 0; i < segments.length; i += 1) {
      const seg = segments[i];
      builtPath = builtPath ? `${builtPath} > ${seg}` : seg;
      let node = pathMap.get(builtPath);
      if (!node) {
        const isLeaf = i === segments.length - 1;
        node = isLeaf ? rowToSlotNode({ ...row, identificador: seg }) : { identificador: seg, children: [] };
        pathMap.set(builtPath, node);
        parentChildren.push(node);
      } else if (i === segments.length - 1) {
        Object.assign(node, rowToSlotNode({ ...row, identificador: seg }));
      }
      if (!node.children) node.children = [];
      parentChildren = node.children;
    }
  }

  for (const [path, node] of pathMap.entries()) {
    if (String(node.identificador || "").trim() === "Exceção") {
      const parentPath = path.replace(/\s*>\s*Exceção\s*$/i, "").trim();
      const parent = pathMap.get(parentPath);
      if (parent) {
        parent.exception = { ...node };
        delete parent.exception.children;
        const idx = (parent.children || []).findIndex((c) => c === node);
        if (idx >= 0) parent.children.splice(idx, 1);
      }
    }
  }

  return {
    version: CHATBOT_FLOW_VERSION,
    format: CHATBOT_FLOW_FORMAT,
    exportedAt: new Date().toISOString(),
    chatbotName,
    slots: roots,
  };
}

export function parseFlowDocument(raw) {
  const data = typeof raw === "string" ? JSON.parse(raw) : raw;
  if (!data || typeof data !== "object") {
    throw new Error("invalid_flow_document");
  }
  if (data.format && data.format !== CHATBOT_FLOW_FORMAT) {
    throw new Error("unsupported_flow_format");
  }
  const version = Number(data.version) || 1;
  if (version < 1 || version > CHATBOT_FLOW_VERSION) {
    throw new Error("unsupported_flow_version");
  }
  if (!Array.isArray(data.slots) || !data.slots.length) {
    throw new Error("empty_flow_slots");
  }
  return data;
}

export function downloadFlowJson(flowDoc, fileName) {
  const safeName = (fileName || "chatbot-fluxo").replace(/[^-\w]+/g, "_");
  const blob = new Blob([JSON.stringify(flowDoc, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = `${safeName}.json`;
  link.click();
  URL.revokeObjectURL(url);
}

export function exportFlowExcel(document, fileName) {
  const rows = flattenTree(document.slots);
  const ws = XLSX.utils.json_to_sheet(rows, { header: EXCEL_HEADERS });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Fluxo");
  const safeName = (fileName || "chatbot-fluxo").replace(/[^-\w]+/g, "_");
  XLSX.writeFile(wb, `${safeName}.xlsx`);
}

export function parseExcelFlowFile(arrayBuffer, chatbotName = "") {
  const wb = XLSX.read(arrayBuffer, { type: "array" });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
  return excelRowsToFlowDocument(rows, chatbotName);
}

/** Documento mínimo de referência para importação JSON/Excel. */
export function getExampleFlowDocument() {
  return {
    version: CHATBOT_FLOW_VERSION,
    format: CHATBOT_FLOW_FORMAT,
    exportedAt: new Date().toISOString(),
    chatbotName: "Exemplo",
    slots: [
      {
        identificador: "Início",
        nome: "Menu principal",
        resposta:
          "Olá! Bem-vindo ao nosso atendimento.\n\nEscolha uma das opções abaixo:",
        botoes: ["Vendas", "Suporte", "Financeiro"],
        condicoes: "Início",
        tipo_acao: 0,
        id_acao: 0,
        global: false,
        ignorarglobal: false,
        ordem: 0,
        children: [
          {
            identificador: "Vendas",
            nome: "Departamento comercial",
            resposta: "Como posso ajudar nas vendas?",
            botoes: ["Falar com consultor", "Ver catálogo"],
            condicoes: "Vendas",
            tipo_acao: 0,
            id_acao: 0,
            global: false,
            ignorarglobal: false,
            ordem: 0,
            children: [],
          },
          {
            identificador: "Suporte",
            nome: "Suporte técnico",
            resposta: "Descreva brevemente o problema ou escolha uma opção:",
            botoes: ["Abrir chamado", "Status do chamado"],
            condicoes: "Suporte",
            tipo_acao: 0,
            id_acao: 0,
            global: false,
            ignorarglobal: false,
            ordem: 1,
            children: [],
          },
          {
            identificador: "Financeiro",
            nome: "Financeiro",
            resposta: "Selecione o assunto financeiro:",
            botoes: ["2ª via de boleto", "Negociar débito"],
            condicoes: "Financeiro",
            tipo_acao: 0,
            id_acao: 0,
            global: false,
            ignorarglobal: false,
            ordem: 2,
            children: [],
          },
        ],
        exception: {
          identificador: "Exceção",
          nome: "",
          resposta: "Não entendi. Por favor, escolha uma opção válida do menu.",
          botoes: [],
          condicoes: null,
          tipo_acao: 0,
          id_acao: 0,
          global: false,
          ignorarglobal: false,
          ordem: 1,
        },
      },
    ],
  };
}

export function downloadExampleFlowJson() {
  downloadFlowJson(getExampleFlowDocument(), "exemplo-chatbot-fluxo");
}

export function downloadExampleFlowExcel() {
  exportFlowExcel(getExampleFlowDocument(), "exemplo-chatbot-fluxo");
}

export function exportFlowVisualPdf(document, fileName) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 40;
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const maxWidth = pageWidth - margin * 2;
  let y = margin;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(`Fluxo | ${document.chatbotName || "ChatBot"}`, margin, y);
  y += 24;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Exportado em ${new Date(document.exportedAt || Date.now()).toLocaleString("pt-BR")}`, margin, y);
  y += 18;
  doc.setTextColor(0);

  const renderNode = (node, depth = 0) => {
    const identificador = String(node.identificador || "Nó").trim();
    const resposta = stripHtml(node.resposta || "");
    const botoes = Array.isArray(node.botoes) ? node.botoes : [];
    const indent = margin + depth * 18;
    const boxWidth = maxWidth - depth * 18;

    ensureSpace(56);
    doc.setDrawColor(180);
    doc.setFillColor(depth === 0 ? 245 : 252, depth === 0 ? 248 : 252, 255);
    doc.roundedRect(indent, y, boxWidth, 44, 4, 4, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(identificador, indent + 10, y + 16);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    const resumo = resposta ? doc.splitTextToSize(resposta.slice(0, 280), boxWidth - 20) : ["(sem resposta)"];
    doc.text(resumo.slice(0, 2), indent + 10, y + 30);

    if (botoes.length) {
      doc.setFontSize(8);
      doc.setTextColor(60);
      doc.text(`Botões: ${botoes.join(" | ")}`, indent + 10, y + 40);
      doc.setTextColor(0);
    }

    y += 52;

    if (node.exception) {
      renderNode({ ...node.exception, identificador: "Exceção" }, depth + 1);
    }
    for (const child of node.children || []) {
      renderNode(child, depth + 1);
    }
  };

  for (const root of document.slots || []) {
    renderNode(root, 0);
    y += 8;
  }

  const safeName = (fileName || "chatbot-fluxo").replace(/[^-\w]+/g, "_");
  doc.save(`${safeName}.pdf`);
}

export function readFileAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("read_failed"));
    reader.readAsArrayBuffer(file);
  });
}

export function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error || new Error("read_failed"));
    reader.readAsText(file);
  });
}

export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error("read_failed"));
    reader.readAsDataURL(file);
  });
}
